import { Link } from 'react-router-dom';
import { Instagram, MapPin } from 'lucide-react';
import { artistProfilePath } from '@/lib/artistSlug';
import { splitArtValues } from '@/lib/artistCategories';
import { resolveIndianArtStarMarker } from '@/lib/indianArtStars';

const uniqueArtValues = (value) => Array.from(new Set(splitArtValues(value)));
const formatArtList = (items) => items.join(' / ');
const formatFollowerCount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const count = Number(value);
  if (!Number.isFinite(count) || count < 0) return '';
  if (count >= 1000000) return `${(count / 1000000).toFixed(count >= 10000000 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(count >= 100000 ? 0 : 1).replace(/\.0$/, '')}K`;
  return count.toLocaleString('en-IN');
};

const RegisteredArtistCard = ({ artist, showDistance = false, spotlight = false, compact = false }) => {
  const imageSrc = artist.image_url || artist.image;
  const categoryItems = uniqueArtValues(artist.category);
  const subcategoryItems = uniqueArtValues(artist.subcategory).filter((item) => !categoryItems.includes(item));
  const displayProfession = String(artist.display_profession || '').trim() || categoryItems[0] || 'Artist';
  const location = artist.city || 'India';
  const openForWork = artist.allow_collaborator_enquiries !== false || artist.allow_artist_requests !== false;
  const instagramFollowerLabel = formatFollowerCount(artist.instagram_followers);
  const speciality = formatArtList(subcategoryItems) || formatArtList(categoryItems) || 'Creative professional';
  const secondaryProfession = subcategoryItems.slice(0, 2).join(' / ')
    || categoryItems.slice(1, 3).join(' / ')
    || '';
  const indianArtStar = resolveIndianArtStarMarker(artist);
  const distanceLabel = showDistance && Number.isFinite(artist.distance_km) && artist.distance_km <= 250
    ? `${artist.distance_km} km away`
    : '';

  if (compact) return (
    <Link to={artistProfilePath(artist)} data-testid={`registered-artist-card-${artist.id}`}
      className="group flex h-full min-w-0 flex-col items-center rounded-2xl border border-orange-100 bg-white p-3 text-center shadow-sm transition hover:-translate-y-1 hover:border-orange-300 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500 sm:p-4">
      <span className="mb-3 rounded-full bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-800">Featured artist</span>
      {imageSrc ? <img src={imageSrc} alt={artist.name} loading="lazy" decoding="async" className="h-20 w-20 rounded-full object-cover ring-2 ring-orange-50" />
        : <span className="flex h-20 w-20 items-center justify-center rounded-full bg-orange-50 text-3xl font-bold text-orange-600">{artist.name?.charAt(0) || 'A'}</span>}
      <h3 className="mt-3 h-6 w-full truncate text-base font-bold leading-6 text-gray-900 sm:text-lg" title={artist.name}>{artist.name}</h3>
      <p className="mt-1 h-5 w-full truncate text-xs font-semibold leading-5 text-orange-700 sm:text-sm" title={displayProfession}>{displayProfession}</p>
      <p className="mt-2 flex h-5 min-w-0 max-w-full items-center justify-center gap-1 text-xs text-gray-500" title={location}><MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /><span className="truncate">{location}</span></p>
      <span className="mt-2 inline-flex h-5 items-center gap-1 text-xs font-semibold text-pink-700">{instagramFollowerLabel && <><Instagram className="h-3.5 w-3.5" aria-hidden="true" />{instagramFollowerLabel}</>}</span>
      <span className="mt-auto w-full pt-4"><span className="block rounded-lg bg-orange-50 px-2 py-2 text-xs font-bold text-orange-700 group-hover:bg-orange-100">View Profile →</span></span>
    </Link>
  );

  return (
    <div
      className={`group artist-card flex h-full flex-col overflow-hidden bg-white transition duration-300 hover:-translate-y-1 hover:shadow-xl ${spotlight ? 'rounded-[2rem] border-2 border-orange-200 shadow-xl' : 'rounded-3xl border border-orange-100 shadow-md'}`}
      data-testid={`registered-artist-card-${artist.id}`}
    >
      <div className={`relative bg-gradient-to-br from-orange-50 via-white to-purple-50 ${spotlight ? 'px-6 pb-5 pt-6' : 'px-5 pb-4 pt-5'}`}>
        <div className="absolute right-3 top-3">
          <span
            className="block max-w-[10rem] truncate rounded-full bg-[#FF6F00] px-3 py-1 text-xs font-bold text-white shadow-sm"
            data-testid={`artist-category-${artist.id}`}
            title={displayProfession}
          >
            {displayProfession}
          </span>
        </div>

        <div className="absolute left-3 top-3">
          <span
            className="flex items-center gap-1 rounded-full bg-green-500 px-3 py-1 text-xs font-bold text-white shadow-sm"
            data-testid={`verified-badge-${artist.id}`}
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Verified
          </span>
        </div>

        <div className={`mx-auto flex items-center justify-center overflow-hidden rounded-full bg-white p-1.5 shadow-xl ring-1 ring-orange-100 transition-transform duration-300 group-hover:scale-[1.03] ${spotlight ? 'mt-8 h-40 w-40' : 'mt-7 h-32 w-32'}`}>
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={`${artist.name} ${speciality} artist profile photo on Kalamanch.art`}
              loading="lazy"
              decoding="async"
              className="h-full w-full rounded-full object-cover object-center"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-full bg-orange-50 text-5xl font-bold text-[#FF6F00] opacity-70">
              {artist.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          {indianArtStar && (
            <span className="rounded-full bg-amber-50 px-3 py-1 text-[11px] font-bold text-amber-700 ring-1 ring-amber-200">
              Star of Indian Art
            </span>
          )}
          <span className={`rounded-full px-3 py-1 text-xs font-bold shadow-sm ${openForWork ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-gray-100 text-gray-500 ring-1 ring-gray-200'}`}>
            {openForWork ? 'Open for Work' : 'Not Open'}
          </span>
          {instagramFollowerLabel && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2.5 py-1 text-xs font-bold text-pink-700 shadow-sm ring-1 ring-pink-200"
              data-testid={`instagram-followers-${artist.id}`}
              aria-label={`${Number(artist.instagram_followers).toLocaleString('en-IN')} Instagram followers`}
              title={`${Number(artist.instagram_followers).toLocaleString('en-IN')} Instagram followers`}
            >
              <Instagram className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{instagramFollowerLabel}</span>
            </span>
          )}
        </div>
      </div>

      <div className={`flex flex-1 flex-col ${spotlight ? 'px-6 pb-6 pt-5' : 'px-5 pb-5 pt-4'}`}>
        <h3
          className={`mb-2 break-words text-center font-bold leading-tight text-gray-900 ${spotlight ? 'text-[clamp(1.3rem,1.55vw,1.6rem)]' : 'text-[clamp(1.1rem,1.35vw,1.35rem)]'}`}
          data-testid={`artist-name-${artist.id}`}
          title={artist.name}
        >
          {artist.name}
        </h3>

        <div className="mb-4 flex flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <p className="text-sm font-bold text-[#C45A00]" title={displayProfession}>
              {displayProfession}
            </p>
            <p className="flex min-w-0 items-center gap-1.5 text-sm text-gray-600" data-testid={`artist-location-${artist.id}`}>
              <MapPin className="h-4 w-4 shrink-0 text-[#FF6F00]" />
              <span className="truncate">{location}</span>
            </p>
          </div>
          <p className="line-clamp-2 text-center text-sm font-semibold leading-6 text-[#4A148C]" data-testid={`artist-speciality-${artist.id}`} title={secondaryProfession || speciality}>
            {secondaryProfession || speciality}
          </p>
          {distanceLabel && (
            <p className="text-center text-sm font-bold text-emerald-700">
              {distanceLabel}
            </p>
          )}
        </div>

        <Link
          to={artistProfilePath(artist)}
          data-testid={`view-profile-${artist.id}`}
          className="mt-auto block"
        >
          <button className="btn-primary w-full whitespace-nowrap py-3 text-center shadow-md shadow-orange-100 transition group-hover:shadow-lg">
            View Profile
          </button>
        </Link>
      </div>
    </div>
  );
};

export default RegisteredArtistCard;
